import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchDeploymentFileUploads } from '../../utils/explorerApi';
import { getFileSignedUrl } from '../../utils/cloudApi';
import { rowsToCsv } from '../../utils/exportUtils';
import { directoryPickerSupported, ensureSubdir, makeUniqueNamer, pickDirectory, writeFileToDir } from '../../utils/explorerExport';

const TYPE_ORDER = ['Images', 'Documents', 'Spreadsheets', 'Presentations', 'Audio', 'Video', 'Archives', 'Other'];
const PAGE_SIZES = [25, 50, 100];

function fileGroup(file) {
    const name = String(file.filename || '').toLowerCase();
    const contentType = String(file.content_type || file.mime_type || '').toLowerCase();
    const ext = name.includes('.') ? name.slice(name.lastIndexOf('.') + 1) : '';
    if (contentType.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'svg', 'tif', 'tiff'].includes(ext)) return 'Images';
    if (contentType.startsWith('audio/') || ['mp3', 'wav', 'm4a', 'aac', 'flac', 'ogg'].includes(ext)) return 'Audio';
    if (contentType.startsWith('video/') || ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(ext)) return 'Video';
    if (contentType.includes('spreadsheet') || ['csv', 'xls', 'xlsx', 'ods'].includes(ext)) return 'Spreadsheets';
    if (contentType.includes('presentation') || ['ppt', 'pptx', 'odp', 'key'].includes(ext)) return 'Presentations';
    if (contentType.includes('zip') || ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)) return 'Archives';
    if (contentType.includes('pdf') || contentType.startsWith('text/') || contentType.includes('word') || ['pdf', 'doc', 'docx', 'rtf', 'odt', 'txt', 'md'].includes(ext)) return 'Documents';
    return 'Other';
}

function fileId(file, index) {
    return String(file.gcs_path || file.id || `${file.session_id || 'session'}:${file.filename || 'file'}:${index}`);
}

function formatSize(size) {
    const bytes = Number(size);
    if (!Number.isFinite(bytes) || bytes < 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileUploadCanvas({ question, deploymentId, responses = [], onExportActionsChange }) {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(25);
    const [page, setPage] = useState(0);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [exportState, setExportState] = useState(null);
    const [exportError, setExportError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setLoadError('');
        fetchDeploymentFileUploads(deploymentId)
            .then((result) => { if (!cancelled) setFiles(Array.isArray(result) ? result : []); })
            .catch((err) => { if (!cancelled) setLoadError(err?.message || 'Unable to load uploaded files.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [deploymentId]);

    const rows = useMemo(() => {
        const questionKeys = new Set([question?.id, question?.name, question?.variable_name].filter(Boolean).map(String));
        const recipientBySession = new Map((responses || []).map((response) => [String(response?.session_id || ''), response?.recipient_id ?? '']));
        return (files || [])
            .filter((file) => questionKeys.has(String(file?.question_id || file?.questionId || file?.variable_name || '')))
            .map((file, index) => {
                const id = fileId(file, index);
                const sessionId = String(file.session_id || '');
                return {
                    ...file,
                    id,
                    type: fileGroup(file),
                    recipient: recipientBySession.get(sessionId) || 'Anonymous',
                    submittedAt: file.uploaded_at || file.created_at || '',
                };
            })
            .sort((a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type)
                || String(a.filename || '').localeCompare(String(b.filename || ''), undefined, { numeric: true }));
    }, [files, question, responses]);

    const filteredRows = useMemo(() => {
        const needle = search.trim().toLowerCase();
        if (!needle) return rows;
        return rows.filter((row) => [row.filename, row.recipient, row.type, row.content_type]
            .some((value) => String(value || '').toLowerCase().includes(needle)));
    }, [rows, search]);

    const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
    const pagedRows = useMemo(() => filteredRows.slice(page * pageSize, (page + 1) * pageSize), [filteredRows, page, pageSize]);
    const allMatchingSelected = filteredRows.length > 0 && filteredRows.every((row) => selectedIds.has(row.id));

    useEffect(() => { setPage((current) => Math.min(current, pageCount - 1)); }, [pageCount]);
    useEffect(() => { setPage(0); }, [search, pageSize]);

    const toggleRow = (id) => setSelectedIds((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const toggleAllMatching = () => setSelectedIds((current) => {
        const next = new Set(current);
        if (allMatchingSelected) filteredRows.forEach((row) => next.delete(row.id));
        else filteredRows.forEach((row) => next.add(row.id));
        return next;
    });

    const exportRows = useCallback(async (scope) => {
        setExportError('');
        let chosen = [];
        if (scope === 'one') {
            chosen = rows.filter((row) => selectedIds.has(row.id));
            if (chosen.length !== 1) {
                setExportError('Select exactly one file to use Export One.');
                return;
            }
        } else if (scope === 'selected') {
            chosen = rows.filter((row) => selectedIds.has(row.id));
            if (!chosen.length) {
                setExportError('Select one or more files to export.');
                return;
            }
        } else {
            chosen = filteredRows;
            if (!chosen.length) {
                setExportError('No matching files to export.');
                return;
            }
        }
        if (!directoryPickerSupported()) {
            setExportError('File export needs Chrome or Edge because this browser cannot choose a folder.');
            return;
        }

        try {
            const root = await pickDirectory();
            const folders = new Map();
            const namers = new Map();
            const manifest = [];
            const errors = [];
            setExportState({ done: 0, total: chosen.length });
            for (let index = 0; index < chosen.length; index += 1) {
                const row = chosen[index];
                const group = row.type;
                try {
                    if (!row.gcs_path) throw new Error('The file has no storage path.');
                    if (!folders.has(group)) folders.set(group, await ensureSubdir(root, group));
                    if (!namers.has(group)) namers.set(group, makeUniqueNamer());
                    const exportedName = namers.get(group)(row.filename || 'file');
                    const signedUrl = await getFileSignedUrl(row.gcs_path, row.filename);
                    const response = await fetch(signedUrl);
                    if (!response.ok) throw new Error(`Download failed (${response.status}).`);
                    await writeFileToDir(folders.get(group), exportedName, await response.arrayBuffer());
                    manifest.push({ exportedFilename: `${group}/${exportedName}`, recipient: row.recipient, question: question?.label || question?.name || '', sourceFilename: row.filename || '', sourceType: row.content_type || row.type, size: row.file_size ?? '', submittedAt: row.submittedAt, error: '' });
                } catch (err) {
                    const message = err?.message || 'Export failed.';
                    errors.push(`${row.filename || 'File'}: ${message}`);
                    manifest.push({ exportedFilename: '', recipient: row.recipient, question: question?.label || question?.name || '', sourceFilename: row.filename || '', sourceType: row.content_type || row.type, size: row.file_size ?? '', submittedAt: row.submittedAt, error: message });
                }
                setExportState({ done: index + 1, total: chosen.length });
            }
            await writeFileToDir(root, 'manifest.csv', '\uFEFF' + rowsToCsv(manifest, [
                { header: 'Exported filename', get: (row) => row.exportedFilename },
                { header: 'Recipient', get: (row) => row.recipient === 'Anonymous' ? '' : row.recipient },
                { header: 'Question', get: (row) => row.question },
                { header: 'Source filename', get: (row) => row.sourceFilename },
                { header: 'Source type', get: (row) => row.sourceType },
                { header: 'Size', get: (row) => row.size },
                { header: 'Submitted timestamp', get: (row) => row.submittedAt },
                { header: 'Export error', get: (row) => row.error },
            ]));
            if (errors.length) setExportError(`${errors.length} file${errors.length === 1 ? '' : 's'} could not be exported. See manifest.csv for details.`);
        } catch (err) {
            setExportError(err?.name === 'AbortError' ? 'Export canceled.' : (err?.message || 'File export failed.'));
        } finally {
            setExportState(null);
        }
    }, [filteredRows, question, rows, selectedIds]);

    useEffect(() => {
        onExportActionsChange?.({
            exportOne: () => exportRows('one'),
            exportSelected: () => exportRows('selected'),
            exportAllMatching: () => exportRows('all'),
            isExporting: !!exportState,
        });
    }, [exportRows, exportState, onExportActionsChange]);

    if (loading) return <div style={messageStyle}>Loading uploaded files…</div>;
    if (loadError) return <div style={{ ...messageStyle, color: '#C62828' }}>{loadError}</div>;

    return (
        <div style={{ fontFamily: 'var(--font-family-primary)', color: '#202124' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <input aria-label="Search uploaded files" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search filename, recipient, or type…" style={searchStyle} />
                <label style={{ fontSize: 12, color: '#5F6368' }}>Rows <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} style={selectStyle}>{PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}</select></label>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#3C4043' }}><input type="checkbox" checked={allMatchingSelected} onChange={toggleAllMatching} /> Select all matching ({filteredRows.length})</label>
                {exportState && <span style={{ fontSize: 12, color: '#5F6368' }}>Exporting {exportState.done}/{exportState.total}…</span>}
            </div>
            {exportError && <div style={{ marginBottom: 10, fontSize: 12, color: '#C62828' }}>{exportError}</div>}
            <div style={{ border: '1px solid #E8EAED', borderRadius: 6, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr style={{ background: '#F8F9FA', color: '#5F6368', textAlign: 'left' }}><th style={thStyle}>Select</th><th style={thStyle}>Type</th><th style={thStyle}>Filename</th><th style={thStyle}>Recipient</th></tr></thead>
                    <tbody>
                        {pagedRows.map((row, index) => <React.Fragment key={row.id}>
                            {(index === 0 || pagedRows[index - 1].type !== row.type) && <tr><td colSpan={4} style={{ padding: '7px 10px', background: '#FAFBFC', color: '#5F6368', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{row.type}</td></tr>}
                            <tr style={{ borderTop: '1px solid #F1F3F4' }}><td style={tdStyle}><input aria-label={`Select ${row.filename || 'file'}`} type="checkbox" checked={selectedIds.has(row.id)} onChange={() => toggleRow(row.id)} /></td><td style={tdStyle}>{row.type}</td><td style={tdStyle}><div style={{ color: '#202124', fontWeight: 500 }}>{row.filename || 'Unnamed file'}</div><div style={{ color: '#80868B', fontSize: 11 }}>{row.content_type || 'Unknown type'}{row.file_size != null ? ` · ${formatSize(row.file_size)}` : ''}</div></td><td style={tdStyle}>{row.recipient}</td></tr>
                        </React.Fragment>)}
                        {!pagedRows.length && <tr><td colSpan={4} style={{ padding: 24, color: '#5F6368', textAlign: 'center' }}>No uploaded files match this question.</td></tr>}
                    </tbody>
                </table>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: 12, color: '#5F6368' }}><span>Page {page + 1} of {pageCount} · {filteredRows.length} file{filteredRows.length === 1 ? '' : 's'}</span><span style={{ display: 'flex', gap: 6 }}><button disabled={page === 0} onClick={() => setPage((current) => current - 1)} style={pagerStyle}>Previous</button><button disabled={page >= pageCount - 1} onClick={() => setPage((current) => current + 1)} style={pagerStyle}>Next</button></span></div>
        </div>
    );
}

const messageStyle = { display: 'flex', minHeight: 220, alignItems: 'center', justifyContent: 'center', color: '#5F6368', fontFamily: 'var(--font-family-primary)' };
const searchStyle = { flex: '1 1 260px', maxWidth: 420, padding: '7px 10px', border: '1px solid #DADCE0', borderRadius: 4, fontSize: 13 };
const selectStyle = { marginLeft: 5, padding: '4px 6px', border: '1px solid #DADCE0', borderRadius: 4, background: '#FFFFFF' };
const thStyle = { padding: '9px 10px', fontWeight: 600, whiteSpace: 'nowrap' };
const tdStyle = { padding: '9px 10px', verticalAlign: 'top' };
const pagerStyle = { padding: '4px 9px', border: '1px solid #DADCE0', borderRadius: 4, background: '#FFFFFF', color: '#3C4043', cursor: 'pointer' };
